import { Injectable } from '@angular/core';
import {State, Action, StateContext, Selector} from '@ngxs/store';
import {
  LoadCategorySuccess,
  LoadEntry,
  LoadEntryFail,
  LoadEntrySuccess,
  LoadIndex,
  LoadIndexFail,
  LoadIndexSuccess,
  LoadList,
  LoadListFail,
  LoadListSuccess,
  LoadSubscription,
  LoadSubscriptionSuccess,
  SendSubscription,
  SendSubscriptionSuccess,
  ConfirmSubscription,
  ConfirmSubscriptionSuccess,
  CancelSubscription,
  CancelSubscriptionSuccess,
  GetSubscriptionByID,
  UpdateSubscription,
} from './blog.actions';
import {
  BlogIndexPage,
  BlogPage,
  getPageUrl,
  getListUrl,
  BlogCategory,
  ListType,
  getPageUrlFromSlug,
  NewsletterSubscription,
  BlogNewsItem} from '../models';
import {BlogService} from '../blog.service';
import {BackendError} from '../../../messages/messages.actions';
import {UnknownError} from '../../../shared/symbols';
import * as Sentry from '@sentry/angular-ivy';
import {slice} from 'lodash';

export class BlogStateModel {
  urlDict: {[key: string]: number};
  itemDict: {[key: number]: BlogPage};
  categoryDict: {[key: string]: BlogCategory};
  indexPage: BlogIndexPage;
  itemLists: {[key: string]: number[]};
  selectedBlogPageUrl: string;
  listValue: string;
  listName: string;
  loading: {[key: string]: boolean};
  loadingBody: {[key: string]: boolean};
  subscription: NewsletterSubscription;
  news: BlogNewsItem[];
  lastIndexLoadingError: UnknownError;
}

const STATE_NAME = 'blog';

@State<BlogStateModel>({
  name: STATE_NAME,
  defaults: {
    urlDict: {},
    itemDict: {},
    categoryDict: {},
    indexPage: null,
    itemLists: {},
    selectedBlogPageUrl: null,
    loading: {},
    loadingBody: {},
    listValue: null,
    listName: null,
    subscription: null,
    news: null,
    lastIndexLoadingError: null,
  }
})

@Injectable()
export class BlogState {

  constructor(public blogService: BlogService) {}

  @Selector()
  static index(state: BlogStateModel): BlogIndexPage {
    return state.indexPage;
  }

  @Selector()
  static recentPostsForRoot(state: BlogStateModel): BlogPage[] {
    if (!state.indexPage) {
      return []
    }
    return slice(state.indexPage.items, 0, 3);
  }

  @Selector()
  static otherPostsForRoot(state: BlogStateModel): BlogPage[] {
    if (!state.indexPage) {
      return []
    }
    return slice(state.indexPage.items, 3);
  }

  @Selector()
  static page(state: BlogStateModel): BlogPage {
    const url = state.selectedBlogPageUrl;
    if (!url) {
      return null;
    }
    const id = state.urlDict[url];
    if (!id) {
      return null;
    }
    return state.itemDict[id];
  }

  @Selector()
  static loading(state: BlogStateModel): boolean {
    return state.selectedBlogPageUrl ? state.loading[state.selectedBlogPageUrl] : null;
  }

  @Selector()
  static loadingBody(state: BlogStateModel): boolean {
    return state.selectedBlogPageUrl ? state.loadingBody[state.selectedBlogPageUrl] : null;
  }

  @Selector()
  static list(state: BlogStateModel): BlogPage[] {
    if (!state.listName || !state.listValue) {
      return null;
    }

    const url = state.selectedBlogPageUrl;
    if (!url || !state.itemLists[url]) {
      return null;
    }

    const ids = state.itemLists[url];
    const itemDict = state.itemDict;
    return ids.map(id => itemDict[id]);
  }

  @Selector()
  static listHeader(state: BlogStateModel): string {
    if (!state.listName || !state.listValue) {
      return null;
    }

    switch(state.listName) {
      case ListType.CATEGORY:
        const value = state.categoryDict[state.listName] ? state.categoryDict[state.listName] : state.listValue;
        return `Category: ${value}`;
      case ListType.TAG:
        return `Tag: ${state.listValue}`;
    }
  }

  @Selector()
  static subscription(state: BlogStateModel): NewsletterSubscription {
    return state.subscription;
  }

  @Selector()
  static news(state: BlogStateModel): BlogNewsItem[] {
    return state.news;
  }

  @Action(LoadIndex)
  loadIndex({getState, patchState, dispatch}: StateContext<BlogStateModel>) {
    const state = getState();
    let patch = {
      selectedBlogPageUrl: 'index'
    };
    if (!state.indexPage) {
      patch['loading'] = {
        ...state.loading,
        index: true,
      };
      this.blogService.loadIndex().subscribe(
        indexPage => dispatch(new LoadIndexSuccess(indexPage)),
        err => dispatch(new LoadIndexFail(err)),
      );
    }
    patchState(patch);
  }

  private updateCategoryDict(categoryDict: any, state: BlogStateModel, entry: BlogPage) {
    for (const category of entry.categories) {
      if (!state.categoryDict[category.slug]) {
        categoryDict[category.slug] = category;
      }
    }
  }

  @Action(LoadIndexSuccess)
  loadIndexSuccess({getState, patchState}: StateContext<BlogStateModel>, {indexPage}: LoadIndexSuccess) {
    const state = getState();

    const urlDict = {};
    const itemDict = {};
    const categoryDict = {};
    for (const entry of indexPage.items) {
      const url = getPageUrl(entry);
      urlDict[url] = entry.id;
      if (!state.itemDict[entry.id]) {
        itemDict[entry.id] = entry;
      }
      this.updateCategoryDict(categoryDict, state, entry);
    }

    patchState({
      loading: {
        ...state.loading,
        index: false,
      },
      indexPage: indexPage,
      urlDict: {
        ...state.urlDict,
        ...urlDict,
      },
      itemDict: {
        ...state.itemDict,
        ...itemDict,
      },
      categoryDict: {
        ...state.categoryDict,
        ...categoryDict,
      },
      news: indexPage.news,
    });
  }

  @Action(LoadIndexFail)
  loadIndexFail({getState, patchState, dispatch}: StateContext<BlogStateModel>, {err}: LoadIndexFail) {
    const state = getState();
    patchState({
      loading: {
        ...state.loading,
        index: false,
      },
      lastIndexLoadingError: err,
    });
    Sentry.captureException(err);
    dispatch(new BackendError(err));
  }

  private getId(state, url: string): number {
    let id = null;
    if (state.urlDict[url]) {
      id = state.urlDict[url];
    }
    return id;
  }

  private getLoadingPatch(state, url: string, value: boolean, id: number = null) {
    const loadingBody = !!(id && state.itemDict[id] && !state.itemDict[id].body_html);
    const patch = {};
    if (loadingBody) {
      patch['loadingBody'] = {
        ...state.loadingBody,
        [url]: value,
      }
    } else {
      patch['loading'] = {
        ...state.loading,
        [url]: value,
      }
    }
    return patch;
  }

  @Action(LoadEntry)
  loadEntry({getState, patchState, dispatch}: StateContext<BlogStateModel>, {slug}: LoadEntry) {
    const state = getState();
    const url = getPageUrlFromSlug(slug);
    let patch = {
      selectedBlogPageUrl: url
    } as Partial<BlogStateModel>;
    const id = this.getId(state, url);
    if (!id || !state.itemDict[id] || !state.itemDict[id].body_html) {
      const loadingPatch = this.getLoadingPatch(state, url, true, id);
      patch = {
        ...patch,
        ...loadingPatch
      };
      this.blogService.loadEntry(slug).subscribe(
        entry => dispatch(new LoadEntrySuccess(url, entry)),
        err => dispatch(new LoadEntryFail(url, err)),
      );
    }

    patchState(patch);
  }

  @Action(LoadEntrySuccess)
  loadEntrySuccess({getState, patchState}: StateContext<BlogStateModel>, {url, entry}: LoadEntrySuccess) {
    const state = getState();
    let id = this.getId(state, url);
    let patch = {};

    if (!id) {
      id = entry.id;
      patch = {
        urlDict: {
          ...state.urlDict,
          [url]: id,
        }
      };
    }
    patch = {
      ...patch,
      ...this.getLoadingPatch(state, url, false, id),
      itemDict: {
        ...state.itemDict,
        [id]: entry,
      },
    };
    patchState(patch);
  }

  @Action(LoadEntryFail)
  loadEntryFail({getState, patchState, dispatch}: StateContext<BlogStateModel>, {url, err}: LoadEntryFail) {
    const state = getState();
    let id = this.getId(state, url);
    patchState(this.getLoadingPatch(state, url, false, id));
    dispatch(new BackendError(err));
  }

  @Action(LoadList)
  loadList({getState, patchState, dispatch}: StateContext<BlogStateModel>, {name, value}: LoadList) {
    const state = getState();
    const url = getListUrl(name, value);
    let patch = {
      selectedBlogPageUrl: url,
      listName: name,
      listValue: value,
    } as Partial<BlogStateModel>;

    if (!state.itemLists[url]) {
      const loadingPatch = this.getLoadingPatch(state, url, true);
      patch = {
        ...patch,
        ...loadingPatch
      };
      this.blogService.loadList(name, value).subscribe(
        entries => dispatch(new LoadListSuccess(url, entries)),
        err => dispatch(new LoadListFail(url, err)),
      );
    }

    patchState(patch);

    if (name === ListType.CATEGORY && !state.categoryDict[value]) {
      this.blogService.loadCategory(value).subscribe(
        category => dispatch(new LoadCategorySuccess(category)),
        err => dispatch(new BackendError(err)),
      )
    }
  }

  @Action(LoadListSuccess)
  loadListSuccess({getState, patchState}: StateContext<BlogStateModel>, {url, entries}: LoadListSuccess) {
    const state = getState();
    const itemDict = state.itemDict;
    const newItemDict = {};
    const urlDict = {};
    const itemList = [];
    for (const entry of entries) {
      const url = getPageUrl(entry);
      urlDict[url] = entry.id;
      if (!itemDict[entry.id]) {
        newItemDict[entry.id] = entry;
      }
      itemList.push(entry.id);
    }

    const patch = {
      ...this.getLoadingPatch(state, url, false),
      urlDict: {
        ...state.urlDict,
        ...urlDict,
      },
      itemDict: {
        ...itemDict,
        ...newItemDict,
      },
      itemLists: {
        ...state.itemLists,
        [url]: itemList,
      }
    };
    patchState(patch);
  }

  @Action(LoadListFail)
  loadListFail({getState, patchState, dispatch}: StateContext<BlogStateModel>, {url, err}: LoadEntryFail) {
    const state = getState();
    patchState(this.getLoadingPatch(state, url, false));
    dispatch(new BackendError(err));
  }

  @Action(LoadCategorySuccess)
  loadCategorySuccess({getState, patchState}: StateContext<BlogStateModel>, {category}: LoadCategorySuccess) {
    const state = getState();
    patchState({
      categoryDict: {
        ...state.categoryDict,
        [category.slug]: category,
      }
    })
  }

  @Action(LoadSubscription)
  loadSubscription({getState, dispatch}: StateContext<BlogStateModel>) {
    const state = getState();
    if (!state.subscription) {
      this.blogService.loadSubscription().subscribe(
        subscriptions => dispatch(new LoadSubscriptionSuccess(subscriptions)),
        err => dispatch(new BackendError(err)),
      );
    }
  }

  @Action(GetSubscriptionByID)
  getSubscriptionByID({dispatch}: StateContext<BlogStateModel>, {subscriptionID}: GetSubscriptionByID) {
    this.blogService.getSubscriptionByID(subscriptionID).subscribe(
      subscriptions => dispatch(new LoadSubscriptionSuccess(subscriptions)),
      err => dispatch(new BackendError(err)),
    );
  }

  @Action(LoadSubscriptionSuccess)
  loadSubscriptionSuccess({patchState}: StateContext<BlogStateModel>, {subscription}: LoadSubscriptionSuccess) {
    patchState({
      subscription
    });
  }

  @Action(SendSubscription)
  sendSubscription({dispatch}: StateContext<BlogStateModel>) {
    this.blogService.sendSubscription().subscribe(
      subscription => dispatch(new SendSubscriptionSuccess(subscription)),
      err => dispatch(new BackendError(err)),
    );
  }

  @Action(SendSubscriptionSuccess)
  sendSubscriptionSuccess({patchState}: StateContext<BlogStateModel>, {subscription}: SendSubscriptionSuccess) {
    patchState({
      subscription
    });
  }

  @Action(ConfirmSubscription)
  confirmSubscription({dispatch}: StateContext<BlogStateModel>, {confirmationData}: ConfirmSubscription) {
    this.blogService.confirmSubscription(confirmationData).subscribe(
      subscription => dispatch(new ConfirmSubscriptionSuccess(subscription)),
      err => dispatch(new BackendError(err)),
    );
  }

  @Action(ConfirmSubscriptionSuccess)
  confirmSubscriptionSuccess({patchState}: StateContext<BlogStateModel>, {subscription}: ConfirmSubscriptionSuccess) {
    patchState({
      subscription
    });
  }

  @Action(CancelSubscription)
  cancelSubscription({dispatch}: StateContext<BlogStateModel>, {cancellationData}: CancelSubscription) {
    this.blogService.cancelSubscription(cancellationData).subscribe(
      subscription => dispatch(new CancelSubscriptionSuccess(subscription)),
      err => dispatch(new BackendError(err)),
    );
  }

  @Action(CancelSubscriptionSuccess)
  cancelSubscriptionSuccess({patchState}: StateContext<BlogStateModel>, {subscription}: CancelSubscriptionSuccess) {
    patchState({
      subscription
    });
  }

  @Action(UpdateSubscription)
  updateSubscription({dispatch}: StateContext<BlogStateModel>, {updateData}: any) {
    this.blogService.updateSubscription(updateData).subscribe(
      subscription => dispatch(new SendSubscriptionSuccess(subscription)),
      err => dispatch(new BackendError(err)),
    );
  }
}
